<template>
  <div class="lesson-forum-topic">
    <inline-loader v-if="isLoading" />
    <template v-else>
      <lesson-forum-topic-resource
        v-for="(topicResource, index) in topicResources"
        :key="index"
        :topic-resource="topicResource"
        :topic="topic"
        :is-loading="false"
        :titles="titles"
        @edit-resource="handleEditResource"
        @delete-resource="handleDeleteResource"
      />
      <cv-button v-if="hasNextPage" class="save" v-bind="dataSaveButton" @click="handleLoadMore">
        <span>{{ t("teacher_reviews_subpage_load_more_button") }}</span>
      </cv-button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { FORUM_TOPIC_RESOURCE_TYPE } from "@modules/lesson-review/lesson.topic.constant.ts";
import LessonReviewApi from "@apis/lesson.review.topic.api";
import LessonForumTopicResource from "@modules/lesson-review/components/LessonForumTopicResource.vue";
import {
  ForumTopicModel,
  ForumTopicResourceModel,
} from "@modules/lesson-review/lesson-review-types.ts";
import { PaginatedResultModel } from "@plugins/common/types.ts";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";

// Props typing
interface Props {
  topic: ForumTopicModel | undefined;
  titles: string[];
}
const props = defineProps<Props>();

// Emits typing
const emit = defineEmits<{
  reloadTopic: [];
}>();

const { t } = useI18n();

// State refs
const isLoading = ref(false);
const currentPageNumber = ref(1);
const pageLastUuid = ref<(number | null)[]>([]);
const topicResourcesData = ref<PaginatedResultModel<ForumTopicResourceModel>>();
const topicResources = ref<ForumTopicResourceModel[]>([]);
const dataSaveButton = ref({
  kind: "primary",
  size: "field",
  loading: false,
  error: false,
  completed: false,
  tipPosition: "bottom",
  tipAlignment: "end",
  errorMessage: t("api_fetch_generic_error_message"),
});

// Computed properties
const hasNextPage = computed<boolean>(() => {
  return topicResourcesData.value ? topicResourcesData.value.hasNextPage : false;
});

// Methods
function handleLoadMore() {
  dataSaveButton.value.loading = true;
  currentPageNumber.value += 1;
  fetchLessonReviewTopicResourceList(getLastUuid(currentPageNumber.value));
}

function getLastUuid(pageNumber: number) {
  currentPageNumber.value = pageNumber;
  if (pageNumber === 1 && pageLastUuid.value.length === 0) pageLastUuid.value.push(null);
  if (pageNumber > pageLastUuid.value.length) {
    const currentPageLastUuid = topicResources.value[topicResources.value.length - 1].id;
    pageLastUuid.value.push(currentPageLastUuid);
    return currentPageLastUuid;
  }
  return undefined;
}

function fetchLessonReviewTopicResourceList(lastUuid?: number | undefined) {
  const requestBody = {
    topicId: props.topic.id,
    topicResourceId: lastUuid || 0,
    pageSize: 12,
  };
  if (
    props.topic.type === FORUM_TOPIC_RESOURCE_TYPE.LESSON_TEACHER_REVIEW ||
    props.topic.type === FORUM_TOPIC_RESOURCE_TYPE.MOE_LIBRARY_TEACHER_REVIEW
  ) {
    LessonReviewApi.fetchLessonTeacherReviewTopicResourceList(requestBody).then(
      (response) => {
        if (response.code !== "ok") {
          isLoading.value = false;
        } else {
          topicResourcesData.value = response.data;
          handleSuccessFetch();
        }
      },
      () => {
        isLoading.value = false;
      },
    );
  } else {
    LessonReviewApi.fetchLessonAdminReviewTopicResourceList(requestBody).then(
      (response) => {
        if (response.code !== "ok") {
          isLoading.value = false;
        } else {
          topicResourcesData.value = response.data;
          handleSuccessFetch();
        }
      },
      () => {
        isLoading.value = false;
      },
    );
  }
}

function handleSuccessFetch() {
  if (topicResourcesData.value != null) {
    topicResourcesData.value.data.forEach((resource) => {
      topicResources.value.push(resource);
    });
  }
  isLoading.value = false;
  dataSaveButton.value.loading = false;
}

function handleDeleteResource(resourceUuid: string) {
  isLoading.value = true;
  currentPageNumber.value = 1;
  topicResources.value = topicResources.value.filter((resource) => {
    return resource.uuid !== resourceUuid;
  });
  isLoading.value = false;
  emit("reloadTopic");
}

function handleEditResource(savedResource: ForumTopicResourceModel) {
  isLoading.value = true;
  currentPageNumber.value = 1;
  const newResourceList: ForumTopicResourceModel[] = [];
  topicResources.value.forEach((resource) => {
    if (resource.uuid == savedResource.uuid) {
      newResourceList.push(savedResource);
    } else {
      newResourceList.push(resource);
    }
  });
  topicResources.value = newResourceList;
  isLoading.value = false;
}

// Lifecycle
fetchLessonReviewTopicResourceList(getLastUuid(currentPageNumber.value));
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.lesson-forum-topic {
  .header {
    display: flex;
    align-items: center;
    .user-info {
      display: flex;
      align-items: center;
      .avatar {
        margin-right: $spacing-03;
      }
      .user-name {
        span {
          @include carbon--type-style("card-title-01");
          display: block;
        }
      }
    }
    .bx--overflow-menu {
      margin-left: auto;
    }
  }
  .date-time {
    @include carbon--type-style("card-short-01");
    margin: $spacing-02 0 $spacing-04 0;
    color: $text-02;
  }
}
</style>
